import { useRef, useCallback, useEffect, useMemo } from "react";
import type { Channel, Program } from "@hybrid-iptv/types";
import { useEpgSettings, OPACITY_MAP, GUIDE_REACH_HOURS } from "./settingsStore.js";
import { EpgGrid } from "./EpgGrid.js";
import { ChannelList } from "./ChannelList.js";
import { EpgScrollRail } from "./EpgScrollRail.js";
import { useGuideFocus } from "./guideFocus.js";
import { useGuideTouchScroll } from "./touchScroll.js";
import type { ChannelProgramIndex } from "./hooks.js";


interface OverlayLayoutProps {
  channels: Channel[];
  selectedChannelId: string | null;
  programIndex: ChannelProgramIndex | null;
  onChannelSelect: (channel: Channel) => void;
  onProgramSelect: (program: Program, channel: Channel) => void;
  /** Long-press / hold-OK / right-click on a programme. */
  onProgramContext?: (program: Program, channel: Channel, point: { x: number; y: number }) => void;
  /** Long-press / hold-OK / right-click on a channel row. */
  onChannelContext?: (channel: Channel, point: { x: number; y: number }) => void;
}

export function OverlayLayout({
  channels,
  selectedChannelId,
  programIndex,
  onChannelSelect,
  onProgramSelect,
  onProgramContext,
  onChannelContext,
}: OverlayLayoutProps) {
  const timeWindow = useEpgSettings((s) => s.timeWindow);
  const opacity = useEpgSettings((s) => s.opacity);
  const overlayOpacity = OPACITY_MAP[opacity];
  // The shade is drawn by the page (app.css), from the top of the screen.
  useEffect(() => {
    document.documentElement.style.setProperty("--epg-overlay-opacity", String(overlayOpacity));
    return () => {
      document.documentElement.style.removeProperty("--epg-overlay-opacity");
    };
  }, [overlayOpacity]);
  const sharedScrollRef = useRef<HTMLDivElement>(null);

  const forwardWheel = useCallback((e: React.WheelEvent) => {
    if (sharedScrollRef.current) {
      sharedScrollRef.current.scrollTop += e.deltaY;
    }
  }, []);
  const touchScroll = useGuideTouchScroll(sharedScrollRef);

  // Roving D-pad focus across the rail and the program grid (TV-first).
  const initialRow = useMemo(() => {
    if (!selectedChannelId) return 0;
    const idx = channels.findIndex((c) => c.id === selectedChannelId);
    return idx >= 0 ? idx : 0;
  }, [channels, selectedChannelId]);
  // The arrows only move the focus ring; OK on a row or block tunes it (see
  // the click handlers below / EpgProgramBlock's key handling).
  const { focus, focusAt, handleKeyDown } = useGuideFocus({
    channels,
    programIndex,
    timeWindowHours: GUIDE_REACH_HOURS,
    sharedScrollRef,
    initialRow,
  });

  return (
    <div
      className="epg-overlay-layout"
      style={{ "--epg-overlay-opacity": overlayOpacity } as React.CSSProperties}
    >
      <div className="epg-overlay-bg" />
      <div className="epg-overlay-content" data-guide="" onKeyDown={handleKeyDown}>
        <EpgScrollRail rowCount={channels.length} scrollRef={sharedScrollRef} />
        <div className="epg-overlay-channels" onWheel={forwardWheel} {...touchScroll}>
          <ChannelList
            channels={channels}
            selectedChannelId={selectedChannelId}
            programIndex={programIndex}
            onSelect={onChannelSelect}
            onContext={onChannelContext}
            virtualScrollRef={sharedScrollRef}
            focusRow={focus.row}
            focusCol={focus.col}
            onFocusAt={focusAt}
          />
        </div>
        <div className="epg-overlay-grid" onWheel={forwardWheel} {...touchScroll}>
          <EpgGrid
            channels={channels}
            programIndex={programIndex}
            timeWindowHours={GUIDE_REACH_HOURS}
            hoursOnScreen={timeWindow}
            selectedChannelId={selectedChannelId}
            sharedScrollRef={sharedScrollRef}
            onProgramSelect={onProgramSelect}
            onProgramContext={onProgramContext}
            onChannelSelect={onChannelSelect}
            onChannelContext={onChannelContext}
            focusRow={focus.row}
            focusCol={focus.col}
            onFocusAt={focusAt}
          />
        </div>
      </div>
    </div>
  );
}
